import { fieldChanged } from 'Shared/editor/field_diff';
import { imageRenditionUrl } from 'Shared/editor/primary_image_field';
import { type IntroVideo, parseIntroVideo } from './intro_video_token';
import type { ApiGuide, ApiGuideItem, BootPrerequisite, GuideEditMetadataProps } from './types';

/** A tool or part row as the details form edits it. */
export type EditableItem = {
   name: string;
   type: string;
   quantity: number;
   notes: string;
   url: string;
   isoptional: boolean;
   /** Whether the item is highlighted as featured on the guide. */
   featured: boolean;
   /**
    * Catalog thumbnail for display only — seeded from the guide GET or a
    * picked suggestion. Not part of the save payload.
    */
   imageUrl?: string | null;
   /**
    * The item wiki page, from the guide GET or the picked suggestion, and null
    * when the item has no page yet. The only trustworthy link target: `url`
    * prefers the store product page, and the wiki title can differ from the
    * display name (e.g. "Phillips #0 Screwdriver" lives at
    * /Item/Phillips_0_Screwdriver), so the slug can't be derived. Display
    * only, never saved.
    */
   wikiUrl?: string | null;
   /**
    * That page's title, and the mirror image of wikiUrl: saved but never
    * shown. `name` holds the display title the picker offered, which the
    * server resolves by title -- so saving the name of an item whose two
    * titles differ finds nothing and creates a duplicate item page. Null for
    * a freehand name, which has no page to name yet.
    */
   wikiTitle?: string | null;
};

type EditableDocument = {
   documentid: number;
   text: string;
   /**
    * document_file_information's classification, seeded from the GET for
    * saved documents and from the picker for ones attached this session.
    * Display-only: buildGuidePatch strips documents to bare ids, so this
    * never reaches the wire. Non-empty string or absent, never '' — the
    * JSON.stringify dirty compare must see one shape from every producer.
    */
   documentType?: string;
};

type EditablePrerequisite = {
   guideid: number;
};

/** Everything the intro and details tabs edit, seeded from GET /guides. */
export type GuideEditFields = {
   title: string;
   category: string;
   subject: string;
   /**
    * The guide type (repair, installation, …). Not on the guide GET, so
    * bootstrapped from props and preserved across step refetches.
    */
   type: string;
   difficulty: string;
   timeRequiredMin: number | null;
   timeRequiredMax: number | null;
   /**
    * Whether the time estimate is a range (two thumbs) rather than a single
    * value. Ephemeral UI state: it isn't stored server-side (a range just
    * means min != max) and buildGuidePatch never sends it, but it rides in
    * the fields so the TimeSlider stays a plain controlled component and
    * resyncs through mergeUntouchedFields like every other field.
    */
   timeIsRange: boolean;
   tools: EditableItem[];
   parts: EditableItem[];
   documents: EditableDocument[];
   prerequisites: EditablePrerequisite[];
   flags: string[];
   isPublic: boolean;
   /**
    * The primary guide photo. Only the imageid is dirty-tracked and sent on
    * PATCH; the url is a display-only rendition (see imageRenditionUrl) that
    * rides along so a just-picked image shows before the next server load.
    * Null falls back to the device image on display and clears
    * guide_meta.imageid on save.
    */
   image: { imageid: number; url: string | null } | null;
   /**
    * The guide's author. Only the userid is dirty-tracked and sent on PATCH
    * (admin-gated server-side); the label and avatar are display-only.
    * Diffing by userid also sidesteps default avatars, whose image objects
    * carry a sentinel imageid and URLs that vary by endpoint. Null only in
    * fixtures — the GET always carries an author.
    */
   author: { userid: number; label: string; imageUrl: string | null } | null;
   /**
    * The managed intro video token, parsed from introduction_raw. Saved by
    * composing it back into the introduction wikitext (the page's save
    * handler), so buildGuidePatch ignores it like tags below.
    */
   video: IntroVideo | null;
   /**
    * Tags and patrol threshold are edited alongside the PATCH fields but
    * save through their own channels (saveTags / saveThreshold AjaxIO), so
    * buildGuidePatch ignores them.
    */
   tags: string[];
   patrolThreshold: number;
};

export function toEditableItem(item: ApiGuideItem): EditableItem {
   return {
      name: item.text,
      type: item.type ?? '',
      quantity: item.quantity ?? 1,
      notes: item.notes ?? '',
      // GET substitutes '#' for itemless urls; don't write that back.
      url: item.url === '#' ? '' : (item.url ?? ''),
      // The API serializes booleans as 0/1; normalize for the checkbox.
      isoptional: Boolean(item.isoptional),
      featured: Boolean(item.featured),
      imageUrl: item.thumbnail,
      wikiUrl: item.wiki_url,
      wikiTitle: item.wiki_title,
   };
}

export function initialGuideFields(
   guide: ApiGuide,
   boot?: {
      metadata?: GuideEditMetadataProps;
      /**
       * The unfiltered prerequisite list bootstrapped by PHP. The API's
       * list omits prereqs the viewer can't see, so seeding from it (and
       * writing the list back wholesale) would silently delete them.
       */
      prerequisites?: BootPrerequisite[];
      /** The current guide type; not carried on the guide GET. */
      guideType?: string;
   }
): GuideEditFields {
   const metadata = boot?.metadata;
   return {
      title: guide.title ?? '',
      category: guide.category ?? '',
      subject: guide.subject ?? '',
      type: boot?.guideType ?? '',
      difficulty: guide.difficulty ?? '',
      // The slider stores "no estimate" as null (commit() maps increment 0
      // to null), so a server-side literal 0 must normalize the same way —
      // otherwise an untouched slider diffs 0 vs null and phantom-dirties
      // the page.
      timeRequiredMin: guide.time_required_min || null,
      timeRequiredMax: guide.time_required_max || null,
      // A stored min != max is a range; seed the toggle to match.
      timeIsRange: (guide.time_required_min || null) !== (guide.time_required_max || null),
      tools: (guide.tools ?? []).map(toEditableItem),
      parts: (guide.parts ?? []).map(toEditableItem),
      documents: (guide.documents ?? []).map(doc => ({
         documentid: doc.documentid,
         text: doc.text,
         documentType: doc.document_type || undefined,
      })),
      prerequisites: (boot?.prerequisites ?? []).map(prereq => ({ guideid: prereq.guideid })),
      flags: (guide.flags ?? []).map(flag => flag.flagid),
      isPublic: Boolean(guide.public),
      image: guide.image ? { imageid: guide.image.id, url: imageRenditionUrl(guide.image) } : null,
      author: guide.author
         ? {
              userid: guide.author.userid,
              label: guide.author.unique_username
                 ? `${guide.author.username} (@${guide.author.unique_username})`
                 : guide.author.username,
              imageUrl: guide.author.image?.mini ?? null,
           }
         : null,
      video: parseIntroVideo(guide.introduction_raw ?? ''),
      tags: metadata?.tags?.tags ?? [],
      // The stored threshold can be the "infinite" sentinel far beyond the
      // display max; clamp like the wiki editor and let the save flow
      // translate the max back to 'inf'.
      patrolThreshold: Math.min(
         metadata?.patrol?.threshold ?? 0,
         metadata?.patrol?.maxDisplayed ?? 0
      ),
   };
}

function itemsPayload(items: EditableItem[]) {
   // An added-but-never-filled row would persist a nameless item (or 422 as
   // a duplicate when there are two); leaving it behind is not a save error.
   return items
      .filter(item => item.name.trim() !== '')
      .map((item, index) => {
         const payload: Record<string, unknown> = {
            // The wiki title when the row came from the catalog: the server
            // looks an item up by title and creates a new page when it misses,
            // so sending the display name in `name` spawns a duplicate.
            name: item.wikiTitle || item.name,
            quantity: item.quantity,
            isoptional: item.isoptional,
            // Always sent: the whole list replaces the stored one, so an
            // omitted featured flag defaults to false server-side and would
            // silently unfeature an item on any unrelated edit. GuidesApi
            // only allows the first item to be featured, so clear the flag on
            // any later row — a drag that moved the featured item down would
            // otherwise 422.
            featured: index === 0 ? item.featured : false,
         };
         // Optional fields ride along only when set.
         if (item.type) {
            payload.type = item.type;
         }
         if (item.notes) {
            payload.notes = item.notes;
         }
         if (item.url) {
            payload.url = item.url;
         }
         return payload;
      });
}

/**
 * Fields where a concurrent editor's change would be silently clobbered.
 *
 * A step add/delete/reorder refetches the guide with a fresh revisionid, and
 * the form advances its concurrency base to match. That's what lets a later
 * guide save reuse the fresh revisionid — but it also means the save can no
 * longer 409 against a field another editor changed before the step op. This
 * spots the overlap the 409 would have caught: a field whose server value
 * moved off our baseline (`fresh` != `baseline`) that the user has also
 * edited to a third value (`fields` differs from both). Untouched fields
 * adopt the server value and aren't conflicts; a local edit that happens to
 * match the server isn't either.
 *
 * Only refreshed fields can register: type, tags, and patrol threshold are
 * copied from the baseline into `fresh` by the caller. Prerequisites come from
 * a separate unfiltered edit-only read and participate in this comparison.
 */
export function conflictingFields(
   fields: GuideEditFields,
   baseline: GuideEditFields,
   fresh: GuideEditFields
): (keyof GuideEditFields)[] {
   return (Object.keys(fresh) as (keyof GuideEditFields)[]).filter(
      key =>
         fieldChanged(fresh, baseline, key) &&
         fieldChanged(fields, baseline, key) &&
         fieldChanged(fields, fresh, key)
   );
}

/**
 * The guide PATCH body, containing only fields the user changed. The
 * introduction and conclusion live in the Tiptap wikitext editors and are
 * appended by the page's save handler when their editors report dirty. Sending
 * untouched fields invites clobbering concurrent edits and validation
 * failures on fields this form never showed — the wiki editor's aggregation
 * bug (#60031) is the cautionary tale here.
 *
 * Wire names match what GuidesApi::updateGuide expects: `category`/`subject`
 * (renamed internally to device/thing), and documents and prerequisites as
 * bare id arrays.
 */
export function buildGuidePatch(
   fields: GuideEditFields,
   initial: GuideEditFields
): Record<string, unknown> {
   const patch: Record<string, unknown> = {};

   if (fieldChanged(fields, initial, 'title')) {
      patch.title = fields.title;
   }
   if (fieldChanged(fields, initial, 'category')) {
      patch.category = fields.category;
   }
   if (fieldChanged(fields, initial, 'subject')) {
      patch.subject = fields.subject;
   }
   if (fieldChanged(fields, initial, 'type')) {
      patch.type = fields.type;
   }
   if (fieldChanged(fields, initial, 'difficulty')) {
      patch.difficulty = fields.difficulty;
   }
   if (
      fieldChanged(fields, initial, 'timeRequiredMin') ||
      fieldChanged(fields, initial, 'timeRequiredMax')
   ) {
      patch.time_required_min = fields.timeRequiredMin ?? 0;
      patch.time_required_max = fields.timeRequiredMax ?? 0;
   }
   if (fieldChanged(fields, initial, 'tools')) {
      patch.tools = itemsPayload(fields.tools);
   }
   if (fieldChanged(fields, initial, 'parts')) {
      patch.parts = itemsPayload(fields.parts);
   }
   if (fieldChanged(fields, initial, 'documents')) {
      patch.documents = fields.documents.map(doc => doc.documentid);
   }
   if (fieldChanged(fields, initial, 'prerequisites')) {
      patch.prerequisites = fields.prerequisites.map(prereq => prereq.guideid);
   }
   if (fieldChanged(fields, initial, 'flags')) {
      patch.flags = fields.flags;
   }
   if (fieldChanged(fields, initial, 'isPublic')) {
      patch.public = fields.isPublic;
   }
   // Diff on the imageid alone, not fieldChanged()'s full-object compare: the
   // stored url is a display-only rendition, and re-picking the same image can
   // resolve a different rendition, which would phantom-dirty the page and
   // send a no-op image PATCH.
   if ((fields.image?.imageid ?? null) !== (initial.image?.imageid ?? null)) {
      patch.image = fields.image?.imageid ?? null;
   }
   // Also userid-only (see the field's doc). A guide always has an author,
   // so unlike the image there is no clearing case to send.
   if (fields.author && fields.author.userid !== (initial.author?.userid ?? null)) {
      patch.author = fields.author.userid;
   }

   return patch;
}
